import React, { useState, useEffect, useCallback, ChangeEvent } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  Shield, 
  ShieldOff, 
  Copy, 
  Download, 
  Upload, 
  Settings, 
  Moon, 
  Sun, 
  Maximize, 
  Minimize, 
  BarChart3,
  Eye,
  EyeOff,
  RefreshCw,
  CheckCircle,
  AlertCircle,
  Key,
  Lock
} from 'lucide-react';

// Type definitions
interface Analytics {
  totalOperations: number;
  successfulEncryptions: number;
  successfulDecryptions: number;
  failedOperations: number;
  avgProcessingTime: number;
}

interface Status {
  type: 'success' | 'error' | '';
  message: string;
}

type Mode = 'encrypt' | 'decrypt';
type KeySize = '128' | '192' | '256';
type Operation = 'encrypt' | 'decrypt';
type TextEncoding = 'utf8' | 'base64' | 'hex';

// AES Encryption/Decryption utilities using Web Crypto API
class AESCrypto {
  private static async deriveKey(password: string, salt: Uint8Array, keySize: number): Promise<CryptoKey> {
    const encoder = new TextEncoder();
    const keyMaterial = await crypto.subtle.importKey(
      'raw',
      encoder.encode(password),
      'PBKDF2',
      false,
      ['deriveKey']
    );

    return crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt: salt,
        iterations: 100000,
        hash: 'SHA-256'
      },
      keyMaterial,
      { name: 'AES-GCM', length: keySize },
      false,
      ['encrypt', 'decrypt']
    );
  }

  static async encrypt(plaintext: string, password: string, keySize: number = 256): Promise<string> {
    try {
      const encoder = new TextEncoder();
      const data = encoder.encode(plaintext);
      
      // Generate random salt and IV
      const salt = crypto.getRandomValues(new Uint8Array(16));
      const iv = crypto.getRandomValues(new Uint8Array(12));
      
      // Derive key
      const key = await this.deriveKey(password, salt, keySize);
      
      // Encrypt
      const encrypted = await crypto.subtle.encrypt(
        { name: 'AES-GCM', iv: iv },
        key,
        data
      );
      
      // Combine salt, iv, and encrypted data
      const result = new Uint8Array(salt.length + iv.length + encrypted.byteLength);
      result.set(salt, 0);
      result.set(iv, salt.length);
      result.set(new Uint8Array(encrypted), salt.length + iv.length);
      
      // Return as base64
      return btoa(String.fromCharCode.apply(null, Array.from(result)));
    } catch (error) {
      throw new Error('AES encryption failed');
    }
  }

  static async decrypt(encryptedData: string, password: string, keySize: number = 256): Promise<string> {
    try {
      // Decode from base64
      const data = new Uint8Array(
        Array.from(atob(encryptedData)).map(char => char.charCodeAt(0))
      );
      
      // Extract salt, iv, and encrypted data
      const salt = data.slice(0, 16);
      const iv = data.slice(16, 28);
      const encrypted = data.slice(28);
      
      // Derive key
      const key = await this.deriveKey(password, salt, keySize);
      
      // Decrypt
      const decrypted = await crypto.subtle.decrypt(
        { name: 'AES-GCM', iv: iv },
        key,
        encrypted
      );
      
      // Decode result
      const decoder = new TextDecoder();
      return decoder.decode(decrypted);
    } catch (error) {
      throw new Error('AES decryption failed - Invalid data or password');
    }
  }
}

const AESEncryptDecrypt: React.FC = () => {
  // Theme and UI state
  const [isDarkMode, setIsDarkMode] = useState<boolean>(false);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [showAdvanced, setShowAdvanced] = useState<boolean>(false);
  const [showAnalytics, setShowAnalytics] = useState<boolean>(false);
  
  // Main state
  const [mode, setMode] = useState<Mode>('encrypt');
  const [inputText, setInputText] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [output, setOutput] = useState<string>('');
  const [showPassword, setShowPassword] = useState<boolean>(false);
  
  // Advanced options
  const [keySize, setKeySize] = useState<KeySize>('256');
  const [inputEncoding, setInputEncoding] = useState<TextEncoding>('utf8');
  const [outputEncoding, setOutputEncoding] = useState<TextEncoding>('base64');
  const [autoConvert, setAutoConvert] = useState<boolean>(true);
  
  // Analytics
  const [analytics, setAnalytics] = useState<Analytics>({
    totalOperations: 0,
    successfulEncryptions: 0,
    successfulDecryptions: 0,
    failedOperations: 0,
    avgProcessingTime: 0
  });
  
  // Status
  const [status, setStatus] = useState<Status>({ type: '', message: '' });
  const [isProcessing, setIsProcessing] = useState<boolean>(false);

  // Initialize theme
  useEffect(() => {
    const savedTheme = localStorage.getItem('aes-tool-theme');
    if (savedTheme) {
      setIsDarkMode(savedTheme === 'dark');
    } else {
      setIsDarkMode(window.matchMedia('(prefers-color-scheme: dark)').matches);
    }
  }, []);

  // Apply theme
  useEffect(() => {
    localStorage.setItem('aes-tool-theme', isDarkMode ? 'dark' : 'light');
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDarkMode]);

  // Update analytics
  const updateAnalytics = useCallback((operation: Operation, success: boolean, processingTime: number): void => {
    setAnalytics(prev => ({
      totalOperations: prev.totalOperations + 1,
      successfulEncryptions: operation === 'encrypt' && success ? prev.successfulEncryptions + 1 : prev.successfulEncryptions,
      successfulDecryptions: operation === 'decrypt' && success ? prev.successfulDecryptions + 1 : prev.successfulDecryptions,
      failedOperations: !success ? prev.failedOperations + 1 : prev.failedOperations,
      avgProcessingTime: (prev.avgProcessingTime * prev.totalOperations + processingTime) / (prev.totalOperations + 1)
    }));
  }, []);

  // Text encoding/decoding utilities
  const encodeText = (text: string, encoding: TextEncoding): string => {
    switch (encoding) {
      case 'base64':
        return btoa(unescape(encodeURIComponent(text)));
      case 'hex':
        return Array.from(new TextEncoder().encode(text))
          .map(byte => byte.toString(16).padStart(2, '0'))
          .join('');
      case 'utf8':
      default:
        return text;
    }
  };

  const decodeText = (text: string, encoding: TextEncoding): string => {
    switch (encoding) {
      case 'base64':
        return decodeURIComponent(escape(atob(text)));
      case 'hex':
        const bytes = new Uint8Array(text.match(/.{1,2}/g)?.map(byte => parseInt(byte, 16)) || []);
        return new TextDecoder().decode(bytes);
      case 'utf8':
      default:
        return text;
    }
  };

  // Process operation (encrypt/decrypt)
  const processOperation = async (): Promise<void> => {
    if (!inputText.trim() || !password.trim()) {
      setStatus({ type: 'error', message: 'Please provide both text data and password' });
      return;
    }

    setIsProcessing(true);
    const startTime = Date.now();

    try {
      let result = '';
      const keySizeNum = parseInt(keySize);
      
      if (mode === 'encrypt') {
        let processedInput = inputText;
        
        // Apply input encoding if needed
        if (inputEncoding !== 'utf8') {
          processedInput = decodeText(inputText, inputEncoding);
        }
        
        result = await AESCrypto.encrypt(processedInput, password, keySizeNum);
        
        // Apply output encoding if needed
        if (outputEncoding !== 'base64' && autoConvert) {
          // Convert from base64 to desired encoding
          const decodedResult = atob(result);
          result = outputEncoding === 'hex' 
            ? Array.from(decodedResult).map(char => char.charCodeAt(0).toString(16).padStart(2, '0')).join('')
            : decodedResult;
        }
        
        setStatus({ type: 'success', message: `Text encrypted successfully with AES-${keySize}!` });
        updateAnalytics('encrypt', true, Date.now() - startTime);
      } else {
        let processedInput = inputText;
        
        // Convert input to base64 if needed
        if (inputEncoding === 'hex') {
          const bytes = inputText.match(/.{1,2}/g)?.map(byte => parseInt(byte, 16)) || [];
          processedInput = btoa(String.fromCharCode(...bytes));
        } else if (inputEncoding === 'utf8') {
          processedInput = btoa(inputText);
        }
        
        result = await AESCrypto.decrypt(processedInput, password, keySizeNum);
        
        // Apply output encoding if needed
        if (outputEncoding !== 'utf8' && autoConvert) {
          result = encodeText(result, outputEncoding);
        }
        
        setStatus({ type: 'success', message: `Text decrypted successfully with AES-${keySize}!` });
        updateAnalytics('decrypt', true, Date.now() - startTime);
      }
      
      setOutput(result);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred';
      setStatus({ type: 'error', message: errorMessage });
      updateAnalytics(mode, false, Date.now() - startTime);
      setOutput('');
    } finally {
      setIsProcessing(false);
    }
  };

  // Copy to clipboard
  const copyToClipboard = async (text: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(text);
      setStatus({ type: 'success', message: 'Copied to clipboard!' });
    } catch (error) {
      setStatus({ type: 'error', message: 'Failed to copy to clipboard' });
    }
  };

  // Download as file
  const downloadFile = (content: string, filename: string): void => {
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setStatus({ type: 'success', message: 'File downloaded successfully!' });
  };

  // Upload file
  const handleFileUpload = (event: ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e: ProgressEvent<FileReader>) => {
        const result = e.target?.result;
        if (typeof result === 'string') {
          setInputText(result);
          setStatus({ type: 'success', message: 'File uploaded successfully!' });
        }
      };
      reader.readAsText(file);
    }
  };

  // Toggle fullscreen
  const toggleFullscreen = async (): Promise<void> => {
    try {
      if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen();
        setIsFullscreen(true);
      } else {
        await document.exitFullscreen();
        setIsFullscreen(false);
      }
    } catch (error) {
      console.error('Error toggling fullscreen:', error);
    }
  };

  // Generate random password
  const generatePassword = (): void => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*()_+-=[]{}|;:,.<>?';
    const length = keySize === '256' ? 32 : keySize === '192' ? 24 : 16;
    let result = '';
    for (let i = 0; i < length; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setPassword(result);
    setStatus({ type: 'success', message: 'Strong random password generated!' });
  };

  // Generate random key
  const generateRandomKey = (): void => {
    const keyLength = parseInt(keySize) / 8; // Convert bits to bytes
    const randomBytes = crypto.getRandomValues(new Uint8Array(keyLength));
    const hexKey = Array.from(randomBytes).map(byte => byte.toString(16).padStart(2, '0')).join('');
    setPassword(hexKey);
    setStatus({ type: 'success', message: `Random ${keySize}-bit AES key generated!` });
  };

  // Handle event changes
  const handleKeySizeChange = (event: ChangeEvent<HTMLSelectElement>): void => {
    setKeySize(event.target.value as KeySize);
  };

  const handleInputEncodingChange = (event: ChangeEvent<HTMLSelectElement>): void => {
    setInputEncoding(event.target.value as TextEncoding);
  };

  const handleOutputEncodingChange = (event: ChangeEvent<HTMLSelectElement>): void => {
    setOutputEncoding(event.target.value as TextEncoding);
  };

  const handleInputTextChange = (event: ChangeEvent<HTMLTextAreaElement>): void => {
    setInputText(event.target.value);
  };

  const handlePasswordChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setPassword(event.target.value);
  };

  const themeClass = isDarkMode ? 'dark' : '';

  return (
    <div className={`min-h-screen transition-colors duration-300 ${themeClass}`}>
      <Helmet>
        <title>AES Encrypt & Decrypt Tool - Advanced Encryption Standard</title>
        <meta name="description" content="Professional AES encryption and decryption tool with multiple key sizes (128, 192, 256-bit). Secure text encryption using Advanced Encryption Standard with Web Crypto API." />
        <meta name="keywords" content="AES encrypt, AES decrypt, Advanced Encryption Standard, AES-128, AES-192, AES-256, text encryption, crypto tool" />
        <meta name="author" content="Crypto Tools" />
        <meta property="og:title" content="AES Encrypt & Decrypt Tool" />
        <meta property="og:description" content="Professional AES encryption tool with 128/192/256-bit key support" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="AES Encrypt & Decrypt Tool" />
        <meta name="twitter:description" content="Advanced AES encryption and decryption tool" />
        <link rel="canonical" href="https://cryptotools.com/aes-encrypt-decrypt" />
      </Helmet>

      <div className="bg-gray-50 dark:bg-gray-900 min-h-screen">
        {/* Header */}
        <header className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
                  <Shield className="h-6 w-6 text-green-600 dark:text-green-400" />
                </div>
                <h1 className="text-xl font-bold text-gray-900 dark:text-white">
                  AES Encrypt & Decrypt
                </h1>
                <span className="px-2 py-1 text-xs font-medium bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 rounded-full">
                  AES-{keySize}
                </span>
              </div>
              
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setShowAnalytics(!showAnalytics)}
                  className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  title="Analytics"
                  type="button"
                >
                  <BarChart3 className="h-5 w-5" />
                </button>
                
                <button
                  onClick={() => setShowAdvanced(!showAdvanced)}
                  className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  title="Advanced Options"
                  type="button"
                >
                  <Settings className="h-5 w-5" />
                </button>
                
                <button
                  onClick={toggleFullscreen}
                  className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  title="Toggle Fullscreen"
                  type="button"
                >
                  {isFullscreen ? <Minimize className="h-5 w-5" /> : <Maximize className="h-5 w-5" />}
                </button>
                
                <button
                  onClick={() => setIsDarkMode(!isDarkMode)}
                  className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  title="Toggle Theme"
                  type="button"
                >
                  {isDarkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
                </button>
              </div>
            </div>
          </div>
        </header>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Status Message */}
          {status.message && (
            <div className={`mb-6 p-4 rounded-lg flex items-center space-x-2 ${
              status.type === 'success' 
                ? 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200 border border-green-200 dark:border-green-800' 
                : 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 border border-red-200 dark:border-red-800'
            }`}>
              {status.type === 'success' ? <CheckCircle className="h-5 w-5" /> : <AlertCircle className="h-5 w-5" />}
              <span>{status.message}</span>
            </div>
          )}

          {/* Analytics Panel */}
          {showAnalytics && (
            <div className="mb-6 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                <BarChart3 className="h-5 w-5 mr-2" />
                Analytics
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600 dark:text-green-400">{analytics.totalOperations}</div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">Total Operations</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{analytics.successfulEncryptions}</div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">Encryptions</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{analytics.successfulDecryptions}</div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">Decryptions</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-red-600 dark:text-red-400">{analytics.failedOperations}</div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">Failed</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">{Math.round(analytics.avgProcessingTime)}ms</div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">Avg Time</div>
                </div>
              </div>
            </div>
          )}

          {/* Advanced Options Panel */}
          {showAdvanced && (
            <div className="mb-6 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                <Settings className="h-5 w-5 mr-2" />
                Advanced Options
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    AES Key Size
                  </label>
                  <select
                    value={keySize}
                    onChange={handleKeySizeChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  >
                    <option value="128">AES-128 (16 bytes)</option>
                    <option value="192">AES-192 (24 bytes)</option>
                    <option value="256">AES-256 (32 bytes)</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Input Encoding
                  </label>
                  <select
                    value={inputEncoding}
                    onChange={handleInputEncodingChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  >
                    <option value="utf8">UTF-8 Text</option>
                    <option value="base64">Base64</option>
                    <option value="hex">Hexadecimal</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Output Encoding
                  </label>
                  <select
                    value={outputEncoding}
                    onChange={handleOutputEncodingChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  >
                    <option value="base64">Base64</option>
                    <option value="hex">Hexadecimal</option>
                    <option value="utf8">UTF-8 Text</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Auto Convert
                  </label>
                  <button
                    onClick={() => setAutoConvert(!autoConvert)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      autoConvert ? 'bg-green-600' : 'bg-gray-200 dark:bg-gray-600'
                    }`}
                    type="button"
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      autoConvert ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Mode Toggle */}
          <div className="mb-6 flex justify-center">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-1 shadow-sm border border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setMode('encrypt')}
                className={`px-6 py-2 rounded-md font-medium transition-colors flex items-center space-x-2 ${
                  mode === 'encrypt'
                    ? 'bg-green-600 text-white shadow-sm'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
                type="button"
              >
                <Shield className="h-4 w-4" />
                <span>Encrypt</span>
              </button>
              <button
                onClick={() => setMode('decrypt')}
                className={`px-6 py-2 rounded-md font-medium transition-colors flex items-center space-x-2 ${
                  mode === 'decrypt'
                    ? 'bg-green-600 text-white shadow-sm'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
                type="button"
              >
                <ShieldOff className="h-4 w-4" />
                <span>Decrypt</span>
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Input Section */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {mode === 'encrypt' ? 'Text to Encrypt' : 'Encrypted Data'}
                  </h3>
                  <div className="flex space-x-2">
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept=".txt,.json"
                        onChange={handleFileUpload}
                        className="hidden"
                      />
                      <div className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
                        <Upload className="h-4 w-4" />
                      </div>
                    </label>
                  </div>
                </div>
              </div>
              
              <div className="p-6">
                <textarea
                  value={inputText}
                  onChange={handleInputTextChange}
                  placeholder={mode === 'encrypt' ? 'Enter your text to encrypt...' : 'Enter encrypted data to decrypt...'}
                  className="w-full h-64 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
                />
                
                {/* Password Input */}
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Password / Key
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={handlePasswordChange}
                      placeholder="Enter encryption/decryption password or key"
                      className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                    <button
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                      type="button"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                {/* Key Generation Buttons */}
                <div className="mt-4 grid grid-cols-2 gap-3">
                  <button
                    onClick={generatePassword}
                    className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors flex items-center justify-center text-sm"
                    type="button"
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Password
                  </button>
                  <button
                    onClick={generateRandomKey}
                    className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors flex items-center justify-center text-sm"
                    type="button"
                  >
                    <Key className="h-4 w-4 mr-2" />
                    AES Key
                  </button>
                </div>

                {/* Process Button */}
                <button
                  onClick={processOperation}
                  disabled={isProcessing}
                  className="w-full mt-4 px-4 py-3 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white rounded-lg font-medium transition-colors flex items-center justify-center space-x-2"
                  type="button"
                >
                  {isProcessing ? (
                    <RefreshCw className="h-4 w-4 animate-spin" />
                  ) : mode === 'encrypt' ? (
                    <Shield className="h-4 w-4" />
                  ) : (
                    <ShieldOff className="h-4 w-4" />
                  )}
                  <span>{isProcessing ? 'Processing...' : mode === 'encrypt' ? `Encrypt with AES-${keySize}` : `Decrypt with AES-${keySize}`}</span>
                </button>
              </div>
            </div>

            {/* Output Section */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {mode === 'encrypt' ? 'Encrypted Result' : 'Decrypted Text'}
                  </h3>
                  {output && (
                    <div className="flex space-x-2">
                      <button
                        onClick={() => copyToClipboard(output)}
                        className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                        title="Copy to clipboard"
                        type="button"
                      >
                        <Copy className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => downloadFile(output, mode === 'encrypt' ? 'encrypted.txt' : 'decrypted.txt')}
                        className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                        title="Download file"
                        type="button"
                      >
                        <Download className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="p-6">
                <textarea
                  value={output}
                  readOnly
                  placeholder={`${mode === 'encrypt' ? 'Encrypted' : 'Decrypted'} result will appear here...`}
                  className="w-full h-64 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-sm resize-none"
                />
                
                {/* Output Info */}
                {output && (
                  <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
                      <span>Output Size: {output.length} characters</span>
                      <span>Encoding: {mode === 'encrypt' ? outputEncoding.toUpperCase() : 'UTF-8'}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Security Information */}
          <div className="mt-8 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
            <div className="flex items-start space-x-3">
              <Lock className="h-6 w-6 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100 mb-2">
                  Security Information
                </h3>
                <div className="text-blue-800 dark:text-blue-200 space-y-2 text-sm">
                  <p>• <strong>AES (Advanced Encryption Standard):</strong> Industry-standard symmetric encryption algorithm approved by NIST</p>
                  <p>• <strong>Key Derivation:</strong> PBKDF2 with SHA-256 and 100,000 iterations for enhanced security</p>
                  <p>• <strong>Mode:</strong> AES-GCM (Galois/Counter Mode) provides both encryption and authentication</p>
                  <p>• <strong>Random Salt & IV:</strong> Each encryption uses unique random salt and initialization vector</p>
                  <p>• <strong>Web Crypto API:</strong> Uses browser's native cryptographic functions for maximum security</p>
                  <p>• <strong>Local Processing:</strong> All encryption/decryption happens in your browser - no data sent to servers</p>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <footer className="mt-12 text-center text-sm text-gray-500 dark:text-gray-400">
            <p>Professional AES encryption tool using Web Crypto API. Your data is processed locally and never transmitted.</p>
          </footer>
        </div>
      </div>
    </div>
  );
};

export default AESEncryptDecrypt;